// @ts-nocheck

import { AnyAction } from 'redux';

import { CustomRouterHistory } from '../createHistory';
import animationsMiddlewareHandler, {
  handledActionTypes as animationsMiddlewareActionTypes,
} from '../modules/animations/middlewares/handlers/animationsMiddlewareHandler';
import BetslipOptionsMiddlewareHandler, {
  handledActionTypes as BetslipOptionsMiddlewareActionTypes,
} from '../modules/betslip-options/middlewares/handlers/BetslipOptionsMiddlewareHandler';
import BoomMiddlewareHandler, {
  handledActionTypes as BoomMiddlewareActionTypes,
} from '../modules/boom/middlewares/handlers/BoomMiddlewareHandler';
import GRMiddlewareHandler, {
  handledActionTypes as GRMiddlewareActionTypes,
} from '../modules/gift-race/middlewares/handlers/GRMiddlewareHandler';
import GRNotificationsMiddlewareHandler, {
  handledActionTypes as GRNotificationsMiddlewareActionTypes,
} from '../modules/gift-race/middlewares/handlers/GRNotificationsMiddlewareHandler';
import KLMiddlewareHandler, {
  handledActionTypes as KLMiddlewareActionTypes,
} from '../modules/koh-lanta/middlewares/handlers/KLMiddlewareHandler';
import lineUpMiddlewareHandler, {
  handledActionTypes as lineUpMiddlewareActionTypes,
} from '../modules/line-up/middlewares/handlers/lineUpMiddleware';
import missionsMiddlewareHandler, {
  handledActionTypes as missionsMiddlewareActionTypes,
} from '../modules/missions/middlewares/handlers/missionsMiddlewareHandler';
import { type StoreType, type GenericAction } from '../types/redux';
import { AnyObject } from '../types/utils';

import apiMiddlewareHandler, {
  handledActionTypes as apiMiddlewareActionTypes,
} from './synchronousHandlers/apiMiddlewareHandler';
import betFilterMiddlewareHandler, {
  handledActionTypes as betFilterMiddlewareActionTypes,
} from './synchronousHandlers/betFilterMiddlewareHandler';
import externalLinkMiddlewareHandler, {
  handledActionTypes as externalLinkMiddlewareActionTypes,
} from './synchronousHandlers/externalLinkMiddlewareHandler';
import favoritesMiddlewareHandler, {
  handledActionTypes as favoritesMiddlewareActionTypes,
} from './synchronousHandlers/favoritesMiddlewareHandler';
import historyItemDetailCashoutMiddlewareHandler, {
  handledActionTypes as historyItemDetailCashoutMiddlewareActionTypes,
} from './synchronousHandlers/historyItemDetailCashoutMiddlewareHandler';
import miscMiddlewareHandler, {
  handledActionTypes as miscMiddlewareActionTypes,
} from './synchronousHandlers/miscMiddlewareHandler';
import mobileDevicesMiddlewareHandler, {
  handledActionTypes as mobileDevicesMiddlewareActionTypes,
} from './synchronousHandlers/mobileDevicesMiddlewareHandler';
import multiviewMiddlewareHandler, {
  handledActionTypes as multiviewMiddlewareActionTypes,
} from './synchronousHandlers/multiviewMiddlewareHandler';
import oneBetslipMiddlewareHandler, {
  handledActionTypes as oneBetslipMiddlewareActionTypes,
} from './synchronousHandlers/oneBetslipMiddlewareHandler';
import onUserSetPreferencesMiddlewareHandler, {
  handledActionTypes as onUserSetPreferencesMiddlewareActionTypes,
} from './synchronousHandlers/onUserSetPreferencesMiddlewareHandler';
import regulationMiddlewareHandler, {
  handledActionTypes as regulationMiddlewareActionTypes,
} from './synchronousHandlers/regulationMiddlewareHandler';
import reportMiddlewareHandler, {
  handledActionTypes as reportMiddlewareActionTypes,
} from './synchronousHandlers/reportMiddlewareHandler';
import reviewMiddlewareHandler, {
  handledActionTypes as reviewMiddlewareActionTypes,
} from './synchronousHandlers/reviewMiddlewareHandler';
import routerMiddlewareHandler, {
  handledActionTypes as routerMiddlewareActionTypes,
} from './synchronousHandlers/routerMiddlewareHandler';
import studioAppMiddlewareHandler, {
  handledActionTypes as studioAppMiddlewareActionTypes,
} from './synchronousHandlers/studioAppMiddlewareHandler';
import trackingMiddlewareHandler, {
  handledActionTypes as trackingMiddlewareActionTypes,
} from './synchronousHandlers/trackingMiddlewareHandler';
import webSocketAsyncMiddlewareHandler, {
  handledActionTypes as webSocketAsyncMiddlewareActionTypes,
} from './synchronousHandlers/webSocketAsyncMiddlewareHandler';

export type HandlerProps = {
  store: StoreType;
  action: AnyAction;
  params: AnyObject & {
    history?: CustomRouterHistory;
  };
};

// Order is important
const metaHandlers = [
  { handler: oneBetslipMiddlewareHandler, types: oneBetslipMiddlewareActionTypes },
  { handler: multiviewMiddlewareHandler, types: multiviewMiddlewareActionTypes },
  { handler: studioAppMiddlewareHandler, types: studioAppMiddlewareActionTypes },
  { handler: apiMiddlewareHandler, types: apiMiddlewareActionTypes },
  { handler: trackingMiddlewareHandler, types: trackingMiddlewareActionTypes },
  { handler: onUserSetPreferencesMiddlewareHandler, types: onUserSetPreferencesMiddlewareActionTypes },
  { handler: reviewMiddlewareHandler, types: reviewMiddlewareActionTypes },
  { handler: historyItemDetailCashoutMiddlewareHandler, types: historyItemDetailCashoutMiddlewareActionTypes },
  { handler: mobileDevicesMiddlewareHandler, types: mobileDevicesMiddlewareActionTypes },
  { handler: favoritesMiddlewareHandler, types: favoritesMiddlewareActionTypes },
  { handler: betFilterMiddlewareHandler, types: betFilterMiddlewareActionTypes },
  { handler: KLMiddlewareHandler, types: KLMiddlewareActionTypes },
  { handler: miscMiddlewareHandler, types: miscMiddlewareActionTypes },
  { handler: externalLinkMiddlewareHandler, types: externalLinkMiddlewareActionTypes },
  { handler: reportMiddlewareHandler, types: reportMiddlewareActionTypes },
  { handler: missionsMiddlewareHandler, types: missionsMiddlewareActionTypes },
  { handler: webSocketAsyncMiddlewareHandler, types: webSocketAsyncMiddlewareActionTypes },
  { handler: animationsMiddlewareHandler, types: animationsMiddlewareActionTypes },
  { handler: GRMiddlewareHandler, types: GRMiddlewareActionTypes },
  { handler: GRNotificationsMiddlewareHandler, types: GRNotificationsMiddlewareActionTypes },
  { handler: lineUpMiddlewareHandler, types: lineUpMiddlewareActionTypes },
  { handler: routerMiddlewareHandler, types: routerMiddlewareActionTypes },
  { handler: BoomMiddlewareHandler, types: BoomMiddlewareActionTypes },
  { handler: BetslipOptionsMiddlewareHandler, types: BetslipOptionsMiddlewareActionTypes },
  { handler: regulationMiddlewareHandler, types: regulationMiddlewareActionTypes },
];

const handlersByActionTypes: { [actionType: string]: Array<(props: HandlerProps) => unknown> } = (() => {
  const res = {};

  for (const metaItem of metaHandlers) {
    const { handler, types } = metaItem;

    for (const type of types) {
      if (res[type]) {
        res[type] = [...res[type], handler];
      } else {
        res[type] = [handler];
      }
    }
  }

  return res;
})();

const synchronousMiddleware =
  (params: unknown) => (store: StoreType) => (next: (action: GenericAction) => unknown) => (action: GenericAction) => {
    const { type } = action;
    const handlersByActionType = handlersByActionTypes[type];

    if (Array.isArray(handlersByActionType)) {
      for (let i = 0; i < handlersByActionType.length; i++) {
        const handler = handlersByActionType[i];

        const returnValue = handler({
          store,
          action,
          params,
        });

        if (returnValue !== undefined) {
          return returnValue;
        }
      }
    }

    return next(action);
  };

export default synchronousMiddleware;
